import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/data/categories';

const iconMap: Record<string, string> = {
  shoes: 'ri-footprint-line',
  hoodies: 'ri-t-shirt-line',
  tshirts: 'ri-t-shirt-line',
  jackets: 'ri-star-line',
  pants: 'ri-ruler-line',
  headwear: 'ri-vip-crown-line',
  sets: 'ri-stack-line',
  underwear: 'ri-heart-line',
  jersey: 'ri-trophy-line',
  accessories: 'ri-sparkling-line',
  bags: 'ri-shopping-bag-line',
  belts: 'ri-links-line',
  perfumes: 'ri-drop-line',
  other: 'ri-more-fill',
};

export default function CategoryDropdown() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelected(cat.id);
    setIsOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="w-full max-w-3xl mx-auto px-4 md:px-6 -mt-10 relative z-20">
      <div className="bg-background-50 rounded-2xl p-5 md:p-8">
        <h2 className="text-lg md:text-2xl font-heading font-semibold text-foreground-900 text-center mb-1 md:mb-2">
          {t('categories.title')}
        </h2>
        <p className="text-xs md:text-sm text-foreground-600 text-center mb-4 md:mb-6">
          {t('categories.subtitle')}
        </p>

        <div ref={ref} className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between px-4 md:px-5 py-3 md:py-3.5 rounded-xl bg-background-100 border border-background-200/70 text-foreground-800 hover:border-accent-300/60 transition-all duration-200 cursor-pointer text-sm"
          >
            <span className={selected ? 'text-foreground-900' : 'text-foreground-500 truncate mr-2'}>
              {selected ? t(categories.find(c => c.id === selected)?.nameKey || '') : t('categories.placeholder')}
            </span>
            <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 transition-transform duration-200 w-5 h-5 flex items-center justify-center flex-shrink-0`}></i>
          </button>

          {isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden z-50 max-h-[60vh] overflow-y-auto">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleSelect(cat)}
                  className="w-full flex items-center gap-3 px-4 md:px-5 py-3 text-left text-sm text-foreground-800 hover:bg-accent-100/60 transition-colors duration-150 cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${iconMap[cat.id] || 'ri-store-line'} text-accent-600 text-sm w-4 h-4 flex items-center justify-center`}></i>
                  </div>
                  {t(cat.nameKey)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
          {categories.slice(0, 6).map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleSelect(cat)}
              className="text-xs px-3 py-1.5 rounded-full bg-background-100 border border-background-200/40 text-foreground-600 hover:bg-accent-100 hover:text-accent-700 hover:border-accent-300/40 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              {t(cat.nameKey)}
            </button>
          ))}
          {categories.length > 6 && (
            <button
              onClick={() => setIsOpen(true)}
              className="text-xs px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200/80 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              +{categories.length - 6} {t('categories.other')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}